import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';

const BookingTermsAndConditions = (): JSX.Element => {
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={2} sx={{ p: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
                    OrderBuddy Booking Terms and Conditions
                </Typography>

                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Last Updated: September 23, 2025
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Version 1.0.0
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Please read these terms and conditions carefully before making any booking through OrderBuddy&apos;s platform.
                </Alert>

                <Divider sx={{ mb: 4 }} />

                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            1. DEFINITIONS AND INTERPRETATION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            In these Terms and Conditions, unless the context otherwise requires:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="'Agreement'"
                                    secondary="means these Terms and Conditions and any other terms expressly incorporated by reference"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'Booking'"
                                    secondary="means a reservation made through the OrderBuddy platform for services or facilities"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'Customer' or 'You'"
                                    secondary="means the person or entity making a booking through our platform"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'OrderBuddy' or 'We' or 'Us'"
                                    secondary="means OrderBuddy Inc., a corporation incorporated under the laws of Delaware"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'Platform'"
                                    secondary="means the OrderBuddy website, mobile application, and related services"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'Service Provider'"
                                    secondary="means the third-party entity providing the actual service or facility being booked"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="'Force Majeure Event'"
                                    secondary="means any event beyond the reasonable control of either party, including but not limited to acts of God, natural disasters, pandemics, government actions, war, terrorism, or industrial disputes"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            2. ACCEPTANCE OF TERMS AND MODIFICATIONS
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            2.1 By accessing or using the OrderBuddy platform, creating an account, or making any booking, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions in their entirety.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            2.2 These Terms and Conditions constitute a legally binding agreement between you and OrderBuddy. If you do not agree to these terms, you must immediately cease using our platform and services.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            2.3 We reserve the right to modify, update, or change these Terms and Conditions at any time without prior notice. Any modifications will be effective immediately upon posting on our platform. Your continued use of the platform after such modifications constitutes your acceptance of the updated terms.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            2.4 It is your responsibility to regularly review these Terms and Conditions to stay informed of any updates or changes.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            3. ELIGIBILITY AND ACCOUNT REGISTRATION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            3.1 To use our services, you must be at least 18 years of age and have the legal capacity to enter into binding contracts. If you are under 18, you may only use our services with the involvement and consent of a parent or legal guardian.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            3.2 You must provide accurate, current, and complete information during the registration process and maintain the accuracy of such information throughout your use of our services.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            3.3 You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            3.4 We reserve the right to suspend or terminate your account if we reasonably suspect fraudulent, abusive, or illegal activity.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            4. BOOKING PROCESS AND CONFIRMATION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            4.1 All bookings are subject to availability and confirmation by the relevant Service Provider. A booking is not confirmed until you receive written confirmation from OrderBuddy via email or through the platform.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            4.2 Booking requests must be made at least 24 hours in advance of the desired service time, unless otherwise specified by the Service Provider.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            4.3 You must provide accurate and complete information for all booking requests, including but not limited to:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="Full name and contact information" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Desired date and time of service" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Number of participants or guests" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Special requirements or accessibility needs" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Payment information" />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            4.4 We reserve the right to refuse any booking request at our sole discretion, including but not limited to situations where:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="The requested service is unavailable" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Incomplete or inaccurate information is provided" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Previous violations of our terms or policies" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Suspected fraudulent activity" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            5. PRICING, PAYMENT, AND FEES
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            5.1 All prices displayed on our platform are in USD unless otherwise specified and include applicable taxes where required by law.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            5.2 Payment must be completed at the time of booking confirmation. We accept the following payment methods:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="Major credit cards (Visa, MasterCard, American Express, Discover)" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Debit cards with major network logos" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Digital wallets (Apple Pay, Google Pay, PayPal)" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Bank transfers (for bookings over $500)" />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            5.3 OrderBuddy charges a service fee for each booking, which will be clearly displayed before payment confirmation. This fee covers platform maintenance, customer support, and transaction processing.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            5.4 Prices may be subject to change without notice, but confirmed bookings will honor the price agreed upon at the time of confirmation.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            5.5 Additional fees may apply for:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="Late cancellations or no-shows" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Changes to existing bookings" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Premium or expedited services" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="International transaction processing" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            6. CANCELLATION, MODIFICATIONS, AND REFUND POLICY
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            6.1 Cancellation requests must be submitted through the OrderBuddy platform or by contacting our customer service team.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            6.2 Standard Cancellation Policy:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText
                                    primary="More than 72 hours before service time: Full refund minus 5% processing fee"
                                    secondary="Refund processed within 5-7 business days"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="48-72 hours before service time: 75% refund"
                                    secondary="25% cancellation fee applies"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="24-48 hours before service time: 50% refund"
                                    secondary="50% cancellation fee applies"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Less than 24 hours or no-show: No refund"
                                    secondary="Full payment forfeited"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            6.3 Premium Service Cancellation Policy (for bookings over $1,000):
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="More than 7 days: Full refund minus 10% processing fee" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="3-7 days: 80% refund" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="1-3 days: 60% refund" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Less than 24 hours: 25% refund" />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            6.4 Modifications to existing bookings may be subject to additional fees and availability. Changes must be requested at least 24 hours before the scheduled service time.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            6.5 Refunds will be processed to the original payment method within 7-14 business days, depending on your financial institution.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            7. USER RESPONSIBILITIES AND CONDUCT
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            7.1 You agree to:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="Arrive punctually for all confirmed bookings" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Treat Service Provider staff and facilities with respect" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Follow all venue rules, regulations, and safety protocols" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Provide accurate information and notify us of any changes" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Pay all fees and charges in full and on time" />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            7.2 You agree not to:
                        </Typography>
                        <List sx={{ ml: 2 }}>
                            <ListItem>
                                <ListItemText primary="Use the platform for any illegal or unauthorized purposes" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Provide false, misleading, or fraudulent information" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Attempt to circumvent payment or booking systems" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Harass, abuse, or threaten other users or Service Providers" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Violate any applicable laws or regulations" />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            7.3 You are fully responsible for any damages, losses, or costs arising from your use of booked services or facilities.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            8. LIMITATION OF LIABILITY AND DISCLAIMERS
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            8.1 OrderBuddy acts solely as an intermediary platform connecting customers with Service Providers. We do not directly provide the services being booked.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            8.2 TO THE MAXIMUM EXTENT PERMITTED BY LAW, ORDERBUDDY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            8.3 Our total liability for any claims arising from or related to your use of our platform shall not exceed the total amount paid by you for the specific booking giving rise to the claim.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            8.4 We shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including but not limited to loss of profits, data, or business opportunities.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            8.5 We are not responsible for the actions, omissions, or performance of Service Providers, and any disputes should be resolved directly with the Service Provider.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            9. PRIVACY AND DATA PROTECTION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            9.1 Your privacy is important to us. Our collection, use, and disclosure of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            9.2 By using our platform, you consent to the collection and use of your information as described in our Privacy Policy.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            9.3 We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            9.4 We may share necessary booking information with Service Providers to facilitate your reservations, but we do not sell your personal information to third parties.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            10. INTELLECTUAL PROPERTY RIGHTS
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            10.1 All content on the OrderBuddy platform, including but not limited to text, graphics, logos, images, software, and design elements, is owned by OrderBuddy or our licensors and is protected by copyright, trademark, and other intellectual property laws.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            10.2 You are granted a limited, non-exclusive, non-transferable license to use our platform solely for personal, non-commercial booking purposes.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            10.3 You may not reproduce, distribute, modify, or create derivative works from any content on our platform without express written permission.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            11. FORCE MAJEURE AND SERVICE INTERRUPTIONS
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            11.1 Neither party shall be liable for any failure or delay in performance due to Force Majeure Events.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            11.2 In the event of service cancellations due to Force Majeure Events, we will work with you and Service Providers to reschedule or provide appropriate refunds.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            11.3 We reserve the right to temporarily suspend or limit access to our platform for maintenance, updates, or security reasons.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            12. DISPUTE RESOLUTION AND GOVERNING LAW
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            12.1 These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to conflict of law principles.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            12.2 Any disputes arising from these terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            12.3 Before initiating formal dispute resolution, parties agree to attempt good faith negotiations for a period of 30 days.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Divider sx={{ mt: 4, mb: 3 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        © 2025 OrderBuddy Inc. All rights reserved.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        These Terms and Conditions constitute the entire agreement between you and OrderBuddy
                        and supersede all prior agreements and understandings.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default BookingTermsAndConditions;